import { ArrowRight, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui';
import type { InviteWorkflowEvent } from '../types';

interface Props {
  events: InviteWorkflowEvent[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  onSelect: (event: InviteWorkflowEvent) => void;
  onRefresh: () => void;
}

const labelForEvent = (event: InviteWorkflowEvent): string =>
  event.display_title || event.event;

export const CandidateActionsList = ({
  events,
  isLoading,
  isFetching,
  error,
  onSelect,
  onRefresh,
}: Props) => {
  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <h3 className="text-base font-semibold text-rgf-gray-900">Your next step</h3>
        <Button
          variant="ghost"
          size="sm"
          icon={<RefreshCcw className="h-4 w-4" />}
          onClick={onRefresh}
          disabled={isFetching}
        >
          Refresh
        </Button>
      </div>
      <div className="card-body">
        {isLoading ? (
          <div className="text-sm text-rgf-gray-500">Loading…</div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error.message || 'We couldn’t load your next step. Please try again.'}
          </div>
        ) : events.length === 0 ? (
          <div className="text-sm text-rgf-gray-600">
            Nothing to do right now — our team will get back to you shortly with the next step.
          </div>
        ) : (
          <ul className="space-y-2">
            {events.map((event) => (
              <li key={event.event}>
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-3 rounded-lg border border-rgf-gray-200 px-4 py-3 text-left hover:border-rgf-primary-400 hover:bg-rgf-primary-50 transition-colors"
                  onClick={() => onSelect(event)}
                >
                  <span className="text-sm font-medium text-rgf-gray-900 min-w-0 flex-1 break-words">
                    {labelForEvent(event)}
                  </span>
                  <ArrowRight className="h-4 w-4 text-rgf-gray-400 flex-shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
